import React, { useEffect, useState } from "react";
import { Loader2, Trash2, LogOut, Upload, Pencil, X } from "lucide-react";
import { api } from "../lib/api";

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password: pw });
      localStorage.setItem("hob_admin_token", data.token);
      onLogin();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FCFAF7] p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-xl" data-testid="admin-login">
        <h1 className="text-2xl font-semibold text-[#2A1112]">Admin Login</h1>
        <p className="mt-1 text-sm text-[#5E4646]">Hari Om Bol — Events</p>
        <input type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="mt-6 w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-3 outline-none focus:border-[#7B1113]" data-testid="admin-email" />
        <input type="password" required placeholder="Password" value={pw} onChange={e => setPw(e.target.value)} className="mt-3 w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-3 outline-none focus:border-[#7B1113]" data-testid="admin-password" />
        {err && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={loading} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#7B1113] px-6 py-3 font-semibold text-[#FCFAF7] disabled:opacity-60" data-testid="admin-login-submit">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
        </button>
      </form>
    </div>
  );
};

const emptyForm = { title: "", description: "", long_description: "", venue: "", event_date: "", event_time: "", event_at: "", amount_inr: "", allow_multiple: true, max_pax_per_booking: 10, poster_data_url: "", active: true };

const Dashboard = ({ onLogout }) => {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("events");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/admin/events").then(r => setEvents(r.data)).catch(() => onLogout());
    api.get("/admin/bookings").then(r => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onPoster = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const url = await fileToDataUrl(file);
    setForm(f => ({ ...f, poster_data_url: url }));
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setErr(""); };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      long_description: ev.long_description || "",
      venue: ev.venue || "",
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      event_at: ev.event_at || "",
      amount_inr: String(ev.amount_inr ?? ""),
      allow_multiple: !!ev.allow_multiple,
      max_pax_per_booking: ev.max_pax_per_booking || 10,
      poster_data_url: ev.poster_data_url || "",
      active: ev.active !== false,
    });
    setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    const payload = { ...form, amount_inr: Number(form.amount_inr), max_pax_per_booking: Number(form.max_pax_per_booking) };
    try {
      if (editingId) await api.put(`/admin/events/${editingId}`, payload);
      else await api.post("/admin/events", payload);
      resetForm(); load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Could not save event"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    if (editingId === id) resetForm();
    load();
  };

  return (
    <div className="min-h-screen bg-[#FCFAF7] p-6 md:p-10" data-testid="admin-dashboard">
      <header className="mx-auto flex max-w-6xl items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#2A1112]">Admin · Events</h1>
        <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-[#7B1113]/15 bg-white px-4 py-2 text-sm font-semibold text-[#7B1113]" data-testid="admin-logout"><LogOut className="h-4 w-4" />Logout</button>
      </header>

      <nav className="mx-auto mt-6 flex max-w-6xl gap-2">
        {["events", "bookings"].map(k => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#7B1113] text-[#FCFAF7]" : "bg-white text-[#2A1112] border border-[#7B1113]/15"}`} data-testid={`admin-tab-${k}`}>{k === "events" ? "Events" : "Bookings"}</button>
        ))}
      </nav>

      <div className="mx-auto mt-6 grid max-w-6xl gap-6 lg:grid-cols-[1fr_1.1fr]">
        {tab === "events" && (
          <>
            <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow" data-testid="event-form">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#2A1112]">{editingId ? "Edit Event" : "New Event"}</h2>
                {editingId && (
                  <button type="button" onClick={resetForm} className="inline-flex items-center gap-1 rounded-full border border-[#7B1113]/15 bg-white px-3 py-1 text-xs font-semibold text-[#7B1113]" data-testid="ef-cancel">
                    <X className="h-3 w-3" /> Cancel
                  </button>
                )}
              </div>
              <div className="mt-4 space-y-3">
                <input required placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-title" />
                <textarea placeholder="Short Description (shown on card)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="min-h-16 w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-desc" />
                <textarea placeholder="Long Description (shown on event detail — supports line breaks)" value={form.long_description} onChange={e => setForm({ ...form, long_description: e.target.value })} className="min-h-36 w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-long-desc" />
                <input placeholder="Venue" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-venue" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5E4646]">Date</label>
                    <input type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-date" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#5E4646]">Time</label>
                    <input type="time" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-time" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input required type="number" min="1" step="0.01" placeholder="Amount per Pax (₹)" value={form.amount_inr} onChange={e => setForm({ ...form, amount_inr: e.target.value })} className="rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113]" data-testid="ef-amount" />
                  <input type="number" min="1" max="50" placeholder="Max Pax/Booking" value={form.max_pax_per_booking} onChange={e => setForm({ ...form, max_pax_per_booking: e.target.value })} disabled={!form.allow_multiple} className="rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5 outline-none focus:border-[#7B1113] disabled:opacity-50" data-testid="ef-maxpax" />
                </div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#2A1112]">
                  <input type="checkbox" checked={form.allow_multiple} onChange={e => setForm({ ...form, allow_multiple: e.target.checked })} data-testid="ef-allow-multi" /> Allow multiple ticket booking
                </label>

                <label className="block cursor-pointer rounded-xl border-2 border-dashed border-[#7B1113]/25 bg-[#FCFAF7] p-4 text-center text-sm text-[#5E4646]" data-testid="ef-poster-label">
                  <Upload className="mx-auto mb-2 h-5 w-5 text-[#7B1113]" />
                  Upload Poster (landscape 16:9)
                  <input type="file" accept="image/*" onChange={onPoster} className="hidden" data-testid="ef-poster-input" />
                </label>
                {form.poster_data_url && <img src={form.poster_data_url} alt="poster preview" className="mx-auto aspect-[16/9] w-full max-w-sm rounded-xl object-cover object-center" data-testid="ef-poster-preview" />}
              </div>

              {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
              <button disabled={saving || !form.poster_data_url} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#7B1113] px-6 py-3 font-semibold text-[#FCFAF7] disabled:opacity-50" data-testid="ef-submit">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editingId ? "Save Changes" : "Create Event"}
              </button>
            </form>

            <div className="space-y-3" data-testid="events-list">
              {events.length === 0 && <p className="rounded-3xl bg-white p-8 text-center text-[#5E4646] shadow">No events yet.</p>}
              {events.map(ev => (
                <div key={ev.id} className="flex items-start gap-4 rounded-3xl bg-white p-4 shadow" data-testid={`admin-event-${ev.id}`}>
                  <img src={ev.poster_data_url} alt="" className="aspect-[16/9] w-28 shrink-0 rounded-xl object-cover object-center" />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-[#2A1112]">{ev.title}</h3>
                    <p className="text-xs text-[#5E4646]">{[ev.event_date, ev.event_time].filter(Boolean).join(" · ") || ev.event_at}</p>
                    <p className="mt-1 text-sm font-semibold text-[#7B1113]">₹{Number(ev.amount_inr).toFixed(2)} / pax · {ev.allow_multiple ? `up to ${ev.max_pax_per_booking}` : "single ticket"}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button onClick={() => startEdit(ev)} className="rounded-full bg-[#F5A623]/15 p-2 text-[#7B1113]" aria-label="edit" data-testid={`admin-event-edit-${ev.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(ev.id)} className="rounded-full bg-red-50 p-2 text-red-600" aria-label="delete" data-testid={`admin-event-del-${ev.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "bookings" && (
          <div className="lg:col-span-2 overflow-hidden rounded-3xl bg-white shadow" data-testid="bookings-list">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F6F2EA] text-xs uppercase tracking-wider text-[#7B1113]">
                <tr><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Event</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Pax</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-t border-[#7B1113]/8" data-testid={`admin-booking-${b.booking_id}`}>
                    <td className="px-4 py-3 font-semibold text-[#2A1112]">{b.booking_id}</td>
                    <td className="px-4 py-3 text-[#5E4646]">{b.event_title}</td>
                    <td className="px-4 py-3 text-[#2A1112]">{b.full_name}</td>
                    <td className="px-4 py-3 text-[#5E4646]">{b.phone}</td>
                    <td className="px-4 py-3 text-[#5E4646]">{b.pax}</td>
                    <td className="px-4 py-3 font-semibold text-[#7B1113]">₹{Number(b.total_inr).toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${b.status === "paid" ? "bg-green-100 text-green-800" : b.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{b.status}</span></td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-[#5E4646]">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("hob_admin_token"));

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/me").catch(() => { localStorage.removeItem("hob_admin_token"); setAuthed(false); });
  }, [authed]);

  const logout = () => { localStorage.removeItem("hob_admin_token"); setAuthed(false); };

  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
